'use client';
import React, { useState, useEffect } from 'react';
import { Translations } from '@/lib/i18n';

interface Props { t: Translations; language?: string; }

interface FAQItem {
  q: string;
  a: string;
}

export default function FAQSection({ t, language = 'de' }: Props) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [faqs, setFaqs] = useState<FAQItem[]>([]);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await fetch(`/api/index.php?route=faq&lang=${language}`);
        const result = await response.json();
        if (result.success && result.data && result.data.length > 0) {
          const loaded = result.data.map((item: { question: string; answer: string }) => ({
            q: item.question,
            a: item.answer,
          }));
          setFaqs(loaded);
          return;
        }
      } catch (err) {
        console.error('FAQ fetch error:', err);
      }
      // Fallback
      setFaqs([
        { q: t.faq1Q, a: t.faq1A },
        { q: t.faq2Q, a: t.faq2A },
        { q: t.faq3Q, a: t.faq3A },
        { q: t.faq4Q, a: t.faq4A },
        { q: t.faq5Q, a: t.faq5A },
        { q: t.faq6Q, a: t.faq6A },
      ]);
    };

    fetchFaqs();
  }, [language, t]);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section data-component-id="src\components\FAQSection.tsx:49:4" data-component-path="src\components\FAQSection.tsx" data-component-line="49" data-component-end-line="102" data-component-file="FAQSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22faq-section%22%2C%22className%22%3A%22py-12%20px-4%22%7D"
      id="faq-section"
      className="py-12 px-4"
      style={{
        backgroundImage: `linear-gradient(rgba(183,183,183,0.5),rgba(183,183,183,0.5)), url('https://b2yg692v.myraidbox.de/wp-content/uploads/2023/06/264608164_4789797834416297_3435562544252592118_n.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: '50% 42%',
      }}
      dir={t.dir}
    >
      <div data-component-id="src\components\FAQSection.tsx:59:6" data-component-path="src\components\FAQSection.tsx" data-component-line="59" data-component-end-line="101" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-5xl%20mx-auto%22%7D" className="max-w-5xl mx-auto">
        <h1 data-component-id="src\components\FAQSection.tsx:60:8" data-component-path="src\components\FAQSection.tsx" data-component-line="60" data-component-end-line="65" data-component-file="FAQSection.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-3xl%20md%3Atext-4xl%20font-bold%20text-center%20mb-2%22%7D"
          className="text-3xl md:text-4xl font-bold text-center mb-2"
          style={{ color: 'rgb(214,0,0)' }}
        >
          {t.faqTitle}
        </h1>
        <h2 data-component-id="src\components\FAQSection.tsx:66:8" data-component-path="src\components\FAQSection.tsx" data-component-line="66" data-component-end-line="66" data-component-file="FAQSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-xl%20text-center%20text-gray-800%20mb-10%20font-semibold%22%7D" className="text-xl text-center text-gray-800 mb-10 font-semibold">{t.faqSubtitle}</h2>

        <div data-component-id="src\components\FAQSection.tsx:68:8" data-component-path="src\components\FAQSection.tsx" data-component-line="68" data-component-end-line="100" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-3%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {faqs.map((faq, i) => (
            <div data-component-id="src\components\FAQSection.tsx:70:12" data-component-path="src\components\FAQSection.tsx" data-component-line="70" data-component-end-line="98" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20rounded-xl%20overflow-hidden%20border-2%20transition-all%22%7D"
              key={i}
              className="bg-white rounded-xl overflow-hidden border-2 transition-all"
              style={{ borderColor: openIndex === i ? 'rgba(214,0,0,0.5)' : 'rgba(3,2,2,0.14)' }}
            >
              <button data-component-id="src\components\FAQSection.tsx:75:14" data-component-path="src\components\FAQSection.tsx" data-component-line="75" data-component-end-line="92" data-component-file="FAQSection.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20flex%20items-center%20justify-between%20px-5%20py-4%20text-left%20transition-colors%20hover%3Abg-gray-50%22%7D"
                onClick={() => toggle(i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50"
              >
                <h4 data-component-id="src\components\FAQSection.tsx:79:16" data-component-path="src\components\FAQSection.tsx" data-component-line="79" data-component-end-line="81" data-component-file="FAQSection.tsx" data-component-name="h4" data-component-content="%7B%22elementName%22%3A%22h4%22%2C%22className%22%3A%22font-semibold%20text-sm%20md%3Atext-base%20text-gray-800%20flex-1%20pr-3%22%7D" className="font-semibold text-sm md:text-base text-gray-800 flex-1 pr-3">
                  {faq.q}
                </h4>
                <svg data-component-id="src\components\FAQSection.tsx:82:16" data-component-path="src\components\FAQSection.tsx" data-component-line="82" data-component-end-line="91" data-component-file="FAQSection.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-3%20h-5%20flex-shrink-0%20transition-transform%22%7D"
                  viewBox="0 0 192 512"
                  className="w-3 h-5 flex-shrink-0 transition-transform"
                  style={{
                    fill: openIndex === i ? 'rgb(214,0,0)' : 'rgb(100,100,100)',
                    transform: openIndex === i ? 'rotate(90deg)' : 'rotate(0deg)',
                  }}
                >
                  <path data-component-id="src\components\FAQSection.tsx:90:18" data-component-path="src\components\FAQSection.tsx" data-component-line="90" data-component-end-line="90" data-component-file="FAQSection.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M0 384.662V127.338c0-17.818 21.543-26.741 34.142-14.142l128.662 128.662c7.81 7.81 7.81 20.474 0 28.284L34.142 398.804C21.543 411.404 0 402.48 0 384.662z" />
                </svg>
              </button>
              {openIndex === i && (
                <div data-component-id="src\components\FAQSection.tsx:94:16" data-component-path="src\components\FAQSection.tsx" data-component-line="94" data-component-end-line="96" data-component-file="FAQSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-5%20pb-4%22%7D" className="px-5 pb-4">
                  <p data-component-id="src\components\FAQSection.tsx:95:18" data-component-path="src\components\FAQSection.tsx" data-component-line="95" data-component-end-line="95" data-component-file="FAQSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-gray-700%20leading-relaxed%22%7D" className="text-sm text-gray-700 leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
